<template>
  <div id="lm-pagination" v-if="isPaginationShowed && !loading">
    <nav aria-label="Page navigation">
      <ul class="pagination">
        <li
          :class="{disabled: isFirstPaginationReached}"
          class="page-item"
          id="first-link-wrapper"
          >
          <span
            @click="setPaginationNumberActivated(1)"
            class="page-link"
            id="first-link"
            >
              <font-awesome-icon
                class="d-inline d-md-none d-lg-none d-xl-none"
                icon="fast-backward"
                />
              <span class="d-none d-md-inline d-lg-inline d-xl-inline" >{{$t('first')}}</span>
          </span>
        </li>
        <li
          :class="{disabled: isFirstPaginationReached}"
          class="page-item"
          id="prev-link-wrapper"
          >
          <a
            @click="setPaginationNumberActivated(--activePagination)"
            class="page-link"
            id="prev-link"
            aria-label="Previous"
            >
            <font-awesome-icon
              class="d-inline d-md-none d-lg-none d-xl-none"
              icon="step-backward"
              />
            <span aria-hidden="true">&laquo;</span>
            <span class="sr-only">Previous</span>
          </a>
        </li>
        <div class="input-group mb-3" id="lm-pagination-numbers-mobile-wrapper">
          <select
            v-model.number="activePagination"
            @change="setPaginationNumberActivated(activePagination)"
            class="custom-select"
            id="lm-pagination-numbers-mobile"
            >
            <option v-for="(number, index) in allPaginationsNumbers" :key="index">{{number}}</option>
          </select>
        </div>
        <ul class="pagination lm-pagination-numbers">
          <li
            v-for="(number, index) in paginationNumbersShowed"
            :key="index"
            :class="{
              active: number === activePagination,
              disabled: number === activePagination
            }"
            class="page-item"
            >
            <a
              @click="setPaginationNumberActivated(null, $event)"
              class="page-link"
              >
                {{number}}
              </a>
          </li>
        </ul>
        <li :class="{disabled: this.isLastPaginationReached}" class="page-item">
          <a
            @click="setPaginationNumberActivated(++activePagination)"
            class="page-link"
            id="next-link"
            aria-label="Next">
            <font-awesome-icon
              class="d-inline d-md-none d-lg-none d-xl-none"
              icon="step-forward"
              />
            <span class="d-none d-md-inline d-lg-inline d-xl-inline" aria-hidden="true">&raquo;</span>
            <span class="sr-only d-none d-md-inline d-lg-inline d-xl-inline">{{$t('next')}}</span>
          </a>
        </li>
        <li
          :class="{disabled: this.isLastPaginationReached}"
          class="page-item"
          id="last-link"
          v-if="allRegistries < elasticSearchLimitQuery"
          >
          <span
            @click="setPaginationNumberActivated(allPaginationsNumbers)" class="page-link"
            >
              <font-awesome-icon
                class="d-inline d-md-none d-lg-none d-xl-none"
                icon="fast-forward"
                />
              <span class="d-none d-md-inline d-lg-inline d-xl-inline" >
                {{$t('last')}}
              </span>
            </span>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import urlQueryBuilder from '@/mixins/urlQueryBuilder.js'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faFastBackward, faFastForward, faStepForward, faStepBackward } from '@fortawesome/free-solid-svg-icons'
library.add(faFastBackward, faFastForward, faStepForward, faStepBackward)

export default {
  mixins: [urlQueryBuilder],
  data () {
    return {
      activePagination: parseInt(this.$route.query.page) ? parseInt(this.$route.query.page) : 1,
      higherActivePaginationBeforeMutate: Math.ceil(this.maxAmountNumbersToShowInPagination / 2)
    }
  },
  computed: {
    ...mapState('commons', ['loading']),
    isPaginationShowed () {
      return this.paginationNumbersShowed.length && (this.activePagination <= this.allPaginationsNumbers)
    },
    allPaginationsNumbers () {
      // eslint-disable-next-line
      this.activePagination = parseInt(this.$route.query.page) ? parseInt(this.$route.query.page) : 1
      if (this.allRegistries > this.elasticSearchLimitQuery) {
        return Math.ceil(this.elasticSearchLimitQuery / this.maxAmountItemsShowsPerPaginationInTable)
      } else {
        return Math.ceil(this.allRegistries / this.maxAmountItemsShowsPerPaginationInTable)
      }
    },
    paginationNumbersShowed () {
      let paginationNumbersShowedArray = []
      let loopIteration = 0
      let firstNumberShowedInPagination = 1
      if (this.maxAmountNumbersToShowInPagination < this.allPaginationsNumbers) {
        if (this.isFirstPartPaginationBeforeMutate) {
          firstNumberShowedInPagination = 1
        } else if (this.isLastPartPaginationBeforeMutate) {
          firstNumberShowedInPagination = this.allPaginationsNumbers - this.maxAmountNumbersToShowInPagination
          ++firstNumberShowedInPagination
        } else {
          firstNumberShowedInPagination = this.activePagination - this.higherActivePaginationBeforeMutate
        }
      }
      for (let i = firstNumberShowedInPagination; i <= this.allPaginationsNumbers; i++) {
        ++loopIteration
        paginationNumbersShowedArray.push(i)
        if (loopIteration === this.maxAmountNumbersToShowInPagination) {
          break
        }
      }
      return paginationNumbersShowedArray
    },
    isLastPaginationReached () {
      if (this.activePagination === this.allPaginationsNumbers) {
        return true
      } else {
        return false
      }
    },
    lastPartPagination () {
      return this.allPaginationsNumbers - (this.maxAmountNumbersToShowInPagination / 2)
    },
    isFirstPaginationReached () {
      if (this.activePagination === 1) {
        return true
      } else {
        return false
      }
    },
    isLastPartPaginationBeforeMutate () {
      return this.activePagination >= this.lastPartPagination
    },
    isFirstPartPaginationBeforeMutate () {
      return this.activePagination <= this.higherActivePaginationBeforeMutate
    }
  },
  props: {
    maxAmountNumbersToShowInPagination: {
      type: Number,
      default: 10
    },
    allRegistries: {
      type: Number,
      required: true
    },
    maxAmountItemsShowsPerPaginationInTable: {
      type: Number,
      required: true
    },
    elasticSearchLimitQuery: {
      type: Number,
      default: 10000
    }
  },
  methods: {
    setPaginationNumberActivated (numberActivated, event) {
      this.activePagination = numberActivated ? Number(numberActivated) : Number(event.target.innerText)
      this.urlQueryBuilder({ page: this.activePagination })
      this.$emit('paginationNumberClick', this.activePagination)
    }
  }
}
</script>

<style scoped>
  #lm-pagination a {
  display: block;
  min-width: 50px;
  text-align: center;
}

#lm-pagination a.page-link {
  border-radius: 5px !important;
}

#lm-pagination .page-link {
  cursor: pointer;
}

#lm-pagination .page-item.active .page-link {
  z-index: 1;
  color: #fff;
  background-color: #007bff;
  border-color: #007bff;
  border-radius: 5px !important;
}

#lm-pagination-numbers-mobile-wrapper {
  display: none;
}

@media (max-width: 1200px) {
  #lm-pagination-numbers-mobile-wrapper {
    display: block;
  }

  #lm-pagination-numbers-mobile-wrapper #lm-pagination-numbers-mobile {
    width: 100%;
  }
}

.lm-pagination-numbers {
  display: flex;
}

@media (max-width: 1200px) {
  .lm-pagination-numbers {
    display: none;
  }
}
</style>
